import { useEffect } from 'react';

/**
 * Arreglo para un fallo de Safari en iPhone (también en la app de iOS).
 *
 * Al escribir en un formulario, iOS desplaza la vista para dejar sitio al
 * teclado. Si el formulario se cierra con el teclado aún abierto (al pulsar
 * Guardar en una ventana modal), a veces no la devuelve a su sitio: la página
 * queda «descolocada» y todo lo fijo (la barra de arriba, la de abajo) aparece
 * desplazado hacia arriba la altura del teclado, hasta recargar.
 *
 * Cuando el teclado se cierra y no queda ningún campo activo, si la vista
 * sigue desplazada, se mueve el scroll un píxel y se devuelve: eso obliga a
 * iOS a recolocarla sin que se note. Fuera de iOS no hay desplazamiento y no hace nada.
 */
export const ArregloTecladoIOS: React.FC = () => {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let espera: number | undefined;

    const escribiendo = () => {
      const a = document.activeElement as HTMLElement | null;
      return !!a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable);
    };

    const recolocar = () => {
      window.clearTimeout(espera);
      // Un poco de margen: iOS anima el cierre del teclado
      espera = window.setTimeout(() => {
        if (escribiendo()) return;
        // Con zoom de pellizco el desplazamiento es legítimo: no tocar
        if (Math.abs(vv.scale - 1) > 0.01) return;
        if (vv.offsetTop > 1 || window.innerHeight - vv.height > 1) {
          // Un empujón de un píxel y vuelta: un scroll al mismo punto iOS lo ignora
          window.scrollBy(0, 1);
          window.scrollBy(0, -1);
        }
      }, 150);
    };

    vv.addEventListener('resize', recolocar);
    document.addEventListener('focusout', recolocar);
    return () => {
      window.clearTimeout(espera);
      vv.removeEventListener('resize', recolocar);
      document.removeEventListener('focusout', recolocar);
    };
  }, []);

  return null;
};
